/**
 * EV Project Service
 *
 * Handles Flux EV API calls:
 * - CRUD for EV scenarios
 * - Simulation & optimization
 * - Vehicle database
 * - Export to Site DNA
 *
 * NOTE: Project CRUD is handled by the unified projects system (useUnifiedProjects).
 * Only EV-specific operations (scenarios, simulation, etc.) remain here.
 *
 * Convention: currentUser is always the last parameter (matches projectService.js)
 */

import { apiGet, apiPost, apiPut, apiDelete, handleApiResponse, getAuthHeaders, API_BASE_URL } from './api';

const EV_API = '/api/flux_ev/v1/ev';

// ============================================================
// Scenarios
// ============================================================

export async function getEVScenarios(projectId, currentUser) {
    const response = await apiGet(`${EV_API}/projects/${projectId}/scenarios`, currentUser);
    const data = await handleApiResponse(response);
    return data.scenarios || [];
}

export async function createEVScenario(projectId, name, currentUser) {
    const response = await apiPost(
        `${EV_API}/projects/${projectId}/scenarios`,
        { name },
        currentUser
    );
    return handleApiResponse(response);
}

export async function updateEVScenario(projectId, scenarioId, updates, currentUser) {
    const response = await apiPut(
        `${EV_API}/projects/${projectId}/scenarios/${scenarioId}`,
        updates,
        currentUser
    );
    return handleApiResponse(response);
}

export async function deleteEVScenario(projectId, scenarioId, currentUser) {
    const response = await apiDelete(
        `${EV_API}/projects/${projectId}/scenarios/${scenarioId}`,
        currentUser
    );
    if (!response.ok) {
        let errorDetail;
        try {
            const data = await response.json();
            errorDetail = data.error || data.message || data.detail;
        } catch (e) {
            // Response body wasn't JSON
        }
        throw new Error(errorDetail || `HTTP ${response.status}: ${response.statusText}`);
    }
}

export async function duplicateEVScenario(projectId, scenarioId, currentUser) {
    const response = await apiPost(
        `${EV_API}/projects/${projectId}/scenarios/${scenarioId}/duplicate`,
        {},
        currentUser
    );
    return handleApiResponse(response);
}

// ============================================================
// Simulation
// ============================================================

export async function simulateProject(projectId, currentUser) {
    const response = await apiPost(
        `${EV_API}/projects/${projectId}/simulate`,
        {},
        currentUser
    );
    return handleApiResponse(response);
}

export async function simulateScenario(projectId, scenarioId, currentUser, siteCapacity) {
    const params = siteCapacity ? `?site_capacity_kw=${siteCapacity}` : '';
    const response = await apiPost(
        `${EV_API}/projects/${projectId}/scenarios/${scenarioId}/simulate${params}`,
        {},
        currentUser
    );
    return handleApiResponse(response);
}

/**
 * Batch simulate scenarios via SSE stream.
 * Returns a Response with a readable body for SSE parsing.
 */
export async function simulateBatch(projectId, scenarioIds, currentUser, siteCapacity) {
    const headers = await getAuthHeaders(currentUser);
    const params = siteCapacity ? `?site_capacity_kw=${siteCapacity}` : '';
    const response = await fetch(
        `${API_BASE_URL}${EV_API}/projects/${projectId}/scenarios/simulate-batch${params}`,
        {
            method: 'POST',
            headers,
            body: JSON.stringify({ scenario_ids: scenarioIds }),
        }
    );
    if (!response.ok) {
        let errorDetail;
        try {
            const data = await response.json();
            errorDetail = data.error || data.message || data.detail;
        } catch (e) {
            // Response body wasn't JSON
        }
        throw new Error(errorDetail || `HTTP ${response.status}: ${response.statusText}`);
    }
    return response;
}

/**
 * Run 365-day annual simulation via SSE stream.
 * Returns a Response with a readable body for SSE parsing.
 */
export async function simulateAnnual(projectId, scenarioId, currentUser) {
    const headers = await getAuthHeaders(currentUser);
    const response = await fetch(
        `${API_BASE_URL}${EV_API}/projects/${projectId}/scenarios/${scenarioId}/simulate-annual`,
        {
            method: 'POST',
            headers,
            body: JSON.stringify({}),
        }
    );
    if (!response.ok) {
        let errorDetail;
        try {
            const data = await response.json();
            errorDetail = data.error || data.message || data.detail;
        } catch (e) {
            // Response body wasn't JSON
        }
        throw new Error(errorDetail || `HTTP ${response.status}: ${response.statusText}`);
    }
    return response;
}

/**
 * Run 365-day annual comparison of all 3 strategies via SSE stream.
 * Returns a Response with a readable body for SSE parsing.
 */
export async function simulateAnnualComparison(projectId, scenarioId, currentUser) {
    const headers = await getAuthHeaders(currentUser);
    const response = await fetch(
        `${API_BASE_URL}${EV_API}/projects/${projectId}/scenarios/${scenarioId}/simulate-annual-comparison`,
        {
            method: 'POST',
            headers,
            body: JSON.stringify({}),
        }
    );
    if (!response.ok) {
        let errorDetail;
        try {
            const data = await response.json();
            errorDetail = data.error || data.message || data.detail;
        } catch (e) {
            // Response body wasn't JSON
        }
        throw new Error(errorDetail || `HTTP ${response.status}: ${response.statusText}`);
    }
    return response;
}

// ============================================================
// Optimization
// ============================================================

// FIX B1: Removed dead ternary that produced the same URL for both branches
export async function optimizeProject(projectId, scenarioId, currentUser) {
    const body = scenarioId ? { scenario_id: scenarioId } : {};
    const response = await apiPost(
        `${EV_API}/projects/${projectId}/optimize`,
        body,
        currentUser
    );
    return handleApiResponse(response);
}

// ============================================================
// Vehicles
// ============================================================

export async function getVehicleDatabase(currentUser) {
    const response = await apiGet(`${EV_API}/projects/vehicles`, currentUser);
    return handleApiResponse(response);
}

// ============================================================
// Chargers
// ============================================================

export async function getChargerDatabase(currentUser) {
    const response = await apiGet(`${EV_API}/chargers`, currentUser);
    return handleApiResponse(response);
}

// ============================================================
// Export to Site DNA
// ============================================================

export async function exportToSiteDNA(evProjectId, options, currentUser) {
    const response = await apiPost(
        `${EV_API}/projects/${evProjectId}/export`,
        options,
        currentUser
    );
    return handleApiResponse(response);
}

export async function getSiteDNAProjects(currentUser) {
    const response = await apiGet('/api/site_dna/v1/projects', currentUser);
    return handleApiResponse(response);
}

// ============================================================
// Import from Flux EV (Site DNA integration)
// ============================================================

export async function getProjectsWithScenarios(currentUser) {
    const response = await apiGet(`${EV_API}/projects/with-scenarios`, currentUser);
    return handleApiResponse(response);
}

export async function getScenarioLoad(projectId, scenarioId, strategy, currentUser) {
    const params = strategy ? `?strategy=${strategy}` : '';
    const response = await apiGet(
        `${EV_API}/projects/${projectId}/scenarios/${scenarioId}/load${params}`,
        currentUser
    );
    return handleApiResponse(response);
}

// ============================================================
// Depot Analysis
// ============================================================

export async function saveDepotToProject(projectId, depotConfig, currentUser) {
    const response = await apiPost(
        `${EV_API}/depot/save-to-project`,
        {
            project_id: projectId,
            ...depotConfig,
        },
        currentUser
    );
    return handleApiResponse(response);
}

export function getReportUrl(projectId, scenarioId) {
    return `${API_BASE_URL}${EV_API}/projects/${projectId}/scenarios/${scenarioId}/report`;
}

export function getExcelExportUrl(projectId, scenarioId) {
    return `${API_BASE_URL}${EV_API}/projects/${projectId}/scenarios/${scenarioId}/export-excel`;
}
